import ReactDOM from "react-dom"
import React, { useEffect, useState, ReactElement } from "react"
import { useA11yDialog } from "react-a11y-dialog"
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import type { BodyScrollOptions } from "body-scroll-lock"
import OnClickLink from "../link-onclick"

export interface ModalWrapperProps {
  id: string
  title: unknown
  buttonText: string
  plainCta?: boolean
  extraWide?: boolean
  children: ReactElement
  closeCallback?: () => void
  openCallback?: () => void
}

const ModalWrapper = (props: ModalWrapperProps) => {
  let targetElement = null
  const contentClasses = `o-modal__content c-modal__content ${
    props.extraWide ? "-extra-wide" : ""
  }`
  const closeCallback = props.closeCallback
  const openCallback = props.openCallback
  const [hasWindow, setHasWindow] = useState<boolean>(false)

  const [instance, attr] = useA11yDialog({
    id: props.id,
    role: "dialog",
    titleId: props.id + "-title",
  })

  const options: BodyScrollOptions = {
    reserveScrollBarGap: true,
  }

  const callCloseCallback = () => {
    if (typeof closeCallback !== "undefined") {
      closeCallback()
    }
  }

  const callOpenCallback = () => {
    if (typeof openCallback !== "undefined") {
      openCallback()
    }
  }

  const onShow = () => {
    targetElement = document.getElementById(props.id)
    disableBodyScroll(targetElement, options)
    instance.show()
  }

  const onHide = () => {
    targetElement = document.getElementById(props.id)
    enableBodyScroll(targetElement)
    instance.hide()
  }

  useEffect(() => {
    if (instance) {
      instance.on("hide", callCloseCallback)
      instance.on("show", callOpenCallback)
      return () => {
        instance.off("hide", callCloseCallback)
        instance.off("show", callOpenCallback)
      }
    }
  }, [instance])

  useEffect(() => {
    // Needed to fix a hydration error
    // useA11yDialog does not support SSR
    // See https://github.com/KittyGiraudel/react-a11y-dialog#server-side-rendering
    if (typeof window !== "undefined") {
      setHasWindow(true)
    }
  }, [])

  const dialog =
    typeof document !== "undefined"
      ? ReactDOM.createPortal(
          <div
            aria-live="assertive"
            {...attr.container}
            className="o-modal__container"
          >
            <div
              {...attr.overlay}
              className="o-modal__overlay c-modal__overlay"
            />
            <div {...attr.dialog} className={contentClasses}>
              <h2 {...attr.title} className="u-visually-hidden">
                {props.buttonText}
              </h2>
              {props.children}
              <button
                {...attr.closeButton}
                className="c-modal__close"
                onClick={onHide}
              >
                <span className="u-visually-hidden">Close modal</span>
              </button>
            </div>
          </div>,
          document.body,
        )
      : null

  const usePlainCta = props.plainCta

  return (
    <>
      {usePlainCta ? (
        <OnClickLink onClick={onShow}>{props.buttonText}</OnClickLink>
      ) : (
        <button className="c-cta -green" type="button" onClick={onShow}>
          {props.buttonText}
        </button>
      )}
      {hasWindow && dialog}
    </>
  )
}

export default ModalWrapper
